import '.n{width:100%;}.ssr-plugin'
import '.d{display:flex;}.ssr-plugin'
import '.ae{justify-content:space-between;}.ssr-plugin'
import '.ed{padding:2rem;}.ssr-plugin'
import { PageLayout } from '@app/pages/layout/PageLayout'
import { useQuery } from '@based/sdk/react'
import { useMemo } from 'react'
import { useLocation } from 'wouter'
import { LeftSidebar } from '@app/components/layout/LeftSidebar'
import { Button } from '@app/components/core/button/Button'
import { ElearningItemSidebar } from './ElearningItemSidebar'
import { ElearningItemContent } from './ElearningItemContent'
import { styled } from 'inlines'
import { useTranslations } from '@i18n/useLanguage'
import { pages } from '@app/util/links'
import { type ListElearningItemModel } from 'functions/app/elearning-item/list'
import { TextLink } from '@app/components/core/link/TextLink'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons'
import { getEntityPageStatus } from '@app/util/getEntityPageStatus'
import { NotFoundPage } from '@app/pages/error/NotFoundPage'
import { PageSeo } from '@app/components/core/seo/PageSeo'

export type ElearningItemPageProps = {
  itemSlug: string
}

export const ElearningItemPage = ({ itemSlug }: ElearningItemPageProps) => {
  const t = useTranslations().app.elearningPage
  const [, setLocation] = useLocation()
  const elearningItemsQuery = useQuery('list-elearning-item')
  const itemQuery = useQuery(
    itemSlug ? 'view-elearning-item' : null,
    itemSlug ? { id: itemSlug } : undefined,
  )
  const elearningItems = useMemo<ListElearningItemModel[]>(
    () => elearningItemsQuery.data ?? [],
    [elearningItemsQuery.data],
  )

  const { prevItem, nextItem } = useMemo(() => {
    const items = elearningItems ?? []
    const currentIndex = items.findIndex((i) => i.slug === itemSlug)

    return {
      prevItem: items[currentIndex - 1],
      nextItem: currentIndex > -1 ? items[currentIndex + 1] : undefined,
    }
  }, [elearningItems, itemSlug])

  if (elearningItemsQuery.loading || !elearningItems.length) {
    return (
      <PageLayout gap="2rem">
        <PageSeo />
        {t.loading}
      </PageLayout>
    )
  }

  if (itemSlug) {
    const itemPageStatus = getEntityPageStatus(itemQuery.loading, itemQuery.data)
    if (itemPageStatus === 'not-found') {
      return <NotFoundPage />
    }
    if (itemPageStatus === 'loading') {
      return (
        <PageLayout gap="2rem">
          <PageSeo />
          {t.loading}
        </PageLayout>
      )
    }
  }

  return (
    <PageLayout gap="0rem">
      <LeftSidebar>
        <LeftSidebar.Sidebar collapsedButtonLabel={t.menu}>
          <ElearningItemSidebar items={elearningItems} activeSlug={itemSlug} />
        </LeftSidebar.Sidebar>

        {itemSlug ? (
          <div className="n">
            <TextLink to={pages.elearning}>
              <FontAwesomeIcon icon={faArrowLeft} />
              Back
            </TextLink>
            <ElearningItemContent id={itemSlug || ''} />
            <div className="d ae"
            >
              <div>
                {prevItem && (
                  <Button
                    variant="secondary"
                    label={t.previous}
                    onClick={() =>
                      setLocation(pages.elearningItem(prevItem.slug))
                    }
                  />
                )}
              </div>
              <div>
                {nextItem && (
                  <Button
                    variant="primary"
                    label={t.next}
                    onClick={() =>
                      setLocation(pages.elearningItem(nextItem.slug))
                    }
                  />
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="ed">{t.selectItem}</div>
        )}
      </LeftSidebar>
    </PageLayout>
  )
}
